import { createEntityAdapter } from '@reduxjs/toolkit';

import { AppError } from '@vk-hr-tek/core/error';

import {
  Link,
  QuickActionCreateEvent,
  WidgetTasksResponse,
  Widget,
  CalendarWidgetCompany,
  BirthdayEmployee,
  WidgetPartition,
  PersonalLink,
  ExternalWidget,
} from '@app/gen/dashboard';
import { DynamicEntity } from '@app/types';

import { ExtendedCustomLinkItem } from '../types';

interface External extends ExternalWidget {
  status: 'idle' | 'loading' | 'complete' | 'failed';
}

export interface DashboardState {
  status: 'idle' | 'loading' | 'complete' | 'failed';
  error: AppError | null;
  layout: Widget[];
  fastActions: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    actions: QuickActionCreateEvent[];
    error: AppError | null;
  };
  fastActionData: {
    modalType: string;
    companyIds?: string[];
    eventTypeIds?: string[];
  } | null;
  electronicServices: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: Link[];
  };
  personalLinks: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: PersonalLink[];
  };
  partitionsEmployee: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: WidgetPartition[];
  };
  partitionsCompany: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: WidgetPartition[];
  };
  external: Record<string, External>;
  taskList: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: WidgetTasksResponse['tasks'];
  };
  calendar: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    birthdays: BirthdayEmployee[];
    companies: CalendarWidgetCompany[];
    companyId: string | undefined;
    includedEmployees: string[];
  };
  widgets: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
  };
  personalLinksSettings: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    name: string | undefined;
    links: PersonalLink[];
  };
  userSection: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    name: string;
    menuItems: ExtendedCustomLinkItem[];
    error: AppError | null;
    needRefetch?: boolean;
  };
}

export const initialState: DashboardState = {
  status: 'idle',
  error: null,
  layout: [],
  fastActions: {
    status: 'idle',
    actions: [],
    error: null,
  },
  fastActionData: null,
  electronicServices: {
    status: 'idle',
    items: [],
  },
  personalLinks: {
    status: 'idle',
    items: [],
  },
  partitionsEmployee: {
    status: 'idle',
    items: [],
  },
  partitionsCompany: {
    status: 'idle',
    items: [],
  },
  external: {},
  taskList: {
    status: 'idle',
    items: [],
  },
  calendar: {
    status: 'idle',
    birthdays: [],
    companies: [],
    companyId: undefined,
    includedEmployees: [],
  },
  widgets: {
    status: 'idle',
    error: null,
  },
  personalLinksSettings: {
    status: 'idle',
    error: null,
    name: undefined,
    links: [],
  },
  userSection: {
    name: '',
    menuItems: [],
    status: 'idle',
    error: null,
    needRefetch: undefined,
  },
};

export const dashboardAdapter = createEntityAdapter<DynamicEntity>();
